import type { ComponentType } from 'react';
import React from 'react';
import type { LoaderFunctionArgs } from 'react-router';
import { createBrowserRouter, Navigate, redirect } from 'react-router';

import { wrapCreateBrowserRouter } from '@sentry/react';

import MFAVerify from '@/lib/auth/components/MFAVerify';
import DashboardLayout from '@/lib/dashboardLayout';
import ApplicationLayout from '@/lib/layout';

import { allAuthProvider } from '../auth/auth';
import ForgotPassword from '../auth/components/ForgotPassword';
import Loader from '../components/Loader/index';
import RootErrorBoundary from '../pages/Error';
import { NewGradLayout } from '../pages/newGrad/newGradLayout';
import NewGrad from '../pages/newGrad/TellAboutYourself';
import { policyLoader } from './policyLoader';

function safeLazy<T>(importFn: () => Promise<{ default: ComponentType<T> }>) {
  const storageKey = importFn.toString();
  let retries = 0;
  const maxRetries = 3;
  const baseDelay = 200; // Start with 200ms
  async function tryImport() {
    try {
      return await importFn();
    } catch (error) {
      if (retries < maxRetries) {
        // TODO: Exponential backoff
        retries += 1;
        const delay = baseDelay * 2 ** retries;
        const jitter = Math.random() * 100;

        await new Promise((resolve) => setTimeout(resolve, delay + jitter));
        retries += 1;
        return tryImport();
      }
      throw error;
    }
  }

  return React.lazy(async () => {
    try {
      const component = await tryImport();

      sessionStorage.removeItem(storageKey);

      return component;
    } catch (error) {
      if (!sessionStorage.getItem(storageKey)) {
        sessionStorage.setItem(storageKey, 'true');
        window.location.reload();
        return { default: () => <Loader /> };
      }

      throw error;
    }
  });
}
const Home = safeLazy(() => import('@/lib/pages/home'));
const PricingPreview = safeLazy(() => import('@/lib/pages/PricingPreview'));

const PolicyDetails = safeLazy(() => import('@/lib/pages/forms/policyDetails'));

const PracticeLocations = safeLazy(
  () => import('@/lib/pages/forms/practiceLocations')
);

const CoverageQuestions = safeLazy(
  () => import('@/lib/pages/newGrad/coverageQuestions')
);

const Summary = safeLazy(() => import('@/lib/pages/newGrad/Summary'));

const Fraud = safeLazy(() => import('@/lib/pages/newGrad/Fraud'));

const Payment = safeLazy(() => import('@/lib/pages/newGrad/Payment'));

const ProfLiabCoverage = safeLazy(
  () => import('@/lib/pages/forms/plCoverageSection')
);

const OwnedEntities = safeLazy(() => import('@/lib/pages/forms/ownedEntities'));

const Epli = safeLazy(() => import('@/lib/pages/forms/epli'));

const CyberLiability = safeLazy(
  () => import('@/lib/pages/forms/cyberLiability/CyberLiability')
);

const GeneralLiability = safeLazy(
  () => import('@/lib/pages/forms/generalLiability')
);

const PracticeOwner = safeLazy(
  () => import('@/lib/pages/forms/plPracticeOwners')
);

const ERISAFidAndEmployee = safeLazy(() => import('@/lib/pages/forms/erisa'));

const CommercialPropertyLocations = safeLazy(
  () => import('@/lib/pages/forms/commercialPropertyLocations')
);

const CommercialPropertyCoverages = safeLazy(
  () => import('@/lib/pages/forms/commercialPropertyCoverages')
);

const PolicyDecisions = safeLazy(
  () => import('@/lib/pages/forms/policyDecisions')
);

const DocumentWarehouse = safeLazy(
  () => import('@/lib/pages/forms/documentWarehouse')
);

const ProfLiabDentist = safeLazy(
  () => import('@/lib/pages/forms/profLiabDentist')
);

const Transactions = safeLazy(() => import('@/lib/pages/forms/transactions'));

const Claims = safeLazy(() => import('@/lib/pages/forms/claims'));

const Login = safeLazy(() => import('@/lib/pages/login'));

const ResetPassword = safeLazy(
  () => import('@/lib/auth/components/ResetPassword')
);

const Page404 = safeLazy(() => import('@/lib/pages/404'));

const Profile = safeLazy(() => import('@/lib/pages/profile/index'));

const PrintQueue = safeLazy(() => import('@/lib/pages/printQueue/index'));

const WorkQueue = safeLazy(() => import('@/lib/pages/workQueue/index'));

const Renewals = safeLazy(() => import('@/lib/pages/renewals/index'));

const Common = safeLazy(() => import('@/lib/pages/forms/commonForms'));

const ExtendedReportingPeriod = safeLazy(
  () => import('@/lib/pages/forms/extendedReportingPeriod')
);

export async function protectedLoader({ request }: LoaderFunctionArgs) {
  // Gate on checkAuth()'s RETURN value, not the mutable `isAuthenticated` flag.
  // A 403 mfa_required (or any non-200) session response returns false here,
  // so we always redirect to login instead of letting the protected page
  // render and fire 401 data queries into the "Ooops!" error boundary.
  const authed = await allAuthProvider.checkAuth();
  if (!authed) {
    const params = new URLSearchParams();
    const reqUrl = new URL(request.url);
    // Preserve the query string too (mirrors mfaRedirect's loginUrlWithFrom), so
    // a path like /policy/123?tab=coverage survives the re-auth round-trip.
    params.set('from', reqUrl.pathname + reqUrl.search);
    return redirect(`/auth/login?${params.toString()}`);
  }

  return allAuthProvider;
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

// TODO: Protect everything within policy
// For loader to execute correctly, the elements must be within the page NOT within the layout!
export const router = sentryCreateBrowserRouter([
  {
    path: 'auth/login',
    element: <Login />,
    loader: async () => {
      try {
        const isAuthenticated = await allAuthProvider.checkAuth();
        if (isAuthenticated) {
          return redirect('/');
        }
        return null;
      } catch {
        return null;
      }
    },
  },
  {
    path: 'auth/reset-password/:userId/:token',
    element: <ResetPassword />,
  },
  {
    path: 'auth/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: 'auth/mfa-verify/:userId',
    element: <MFAVerify />,
  },
  {
    path: 'newgrad/:pk?',
    element: <NewGradLayout />,
    children: [
      {
        index: true,
        path: '1/:agency_id?',
        element: <NewGrad />,
      },
      {
        path: '2/:agency_id?',
        element: <CoverageQuestions />,
      },
      {
        path: '3/:agency_id?',
        element: <Summary />,
      },
      {
        path: '4/:agency_id?',
        element: <Fraud />,
      },
      {
        path: '5/:agency_id?',
        element: <Payment />,
      },
      {
        path: '',
        element: <Navigate to="/newgrad/1" />,
      },
    ],
  },
  {
    path: 'newgrade/*',
    element: <Navigate to="/newgrad/1" />,
  },
  {
    path: '*',
    element: <Page404 />,
  },

  {
    id: 'root',
    element: <DashboardLayout />,
    path: '/',
    loader: protectedLoader,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'load-renewals',
        element: <Renewals />,
      },
      {
        path: 'profile',
        element: <Profile />,
        loader: protectedLoader,
      },
      {
        path: 'print-queue',
        element: <PrintQueue />,
        loader: protectedLoader,
      },
      {
        path: 'search',
        element: <WorkQueue />,
        loader: protectedLoader,
      },
    ],
  },
  {
    id: 'policy-root',
    loader: protectedLoader,
    path: '/policy/:id',
    element: <ApplicationLayout />,
    errorElement: <RootErrorBoundary />,

    children: [
      {
        path: 'pricing-preview',
        element: <PricingPreview />,
        loader: policyLoader,
      },
      {
        path: 'policy-details',
        element: <PolicyDetails />,
        loader: policyLoader,
      },
      {
        path: 'practice-locations',
        element: <PracticeLocations />,
        loader: policyLoader,
      },
      {
        path: 'common',
        element: <Common />,
        loader: policyLoader,
      },

      {
        path: 'practice-locations/:locationId',
        element: <PracticeLocations />,
        loader: policyLoader,
      },
      {
        path: 'prof-liability-coverage',
        element: <ProfLiabCoverage />,
        loader: policyLoader,
      },
      {
        path: 'prof-liability-dentist',
        element: <ProfLiabDentist />,
        loader: policyLoader,
      },
      {
        path: 'prof-liability-dentist/:dentistId',
        element: <ProfLiabDentist />,
        loader: policyLoader,
      },

      {
        path: 'prof-liability-practice-owners',
        element: <PracticeOwner />,
        loader: policyLoader,
      },
      {
        path: 'owned-entities',
        element: <OwnedEntities />,
        loader: policyLoader,
      },
      {
        path: 'owned-entities/:entityId',
        element: <OwnedEntities />,
        loader: policyLoader,
      },

      {
        path: 'cyber-liability',
        element: <CyberLiability />,
        loader: policyLoader,
      },
      {
        path: 'epli',
        element: <Epli />,
        loader: policyLoader,
      },
      {
        path: 'general-liability',
        loader: policyLoader,
        element: <GeneralLiability />,
      },
      {
        path: 'erisa-fiduciary-and-employee-benefits',
        element: <ERISAFidAndEmployee />,
        loader: policyLoader,
      },
      {
        path: 'commercial-property-locations',
        element: <CommercialPropertyLocations />,
        loader: policyLoader,
      },
      {
        path: 'commercial-property-locations/:locationId',
        element: <CommercialPropertyLocations />,
        loader: policyLoader,
      },
      {
        path: 'commercial-property-coverages',
        element: <CommercialPropertyCoverages />,
        loader: policyLoader,
      },
      {
        path: 'policy-decisions',
        element: <PolicyDecisions />,

        loader: policyLoader,
      },
      {
        path: 'document-warehouse',
        element: <DocumentWarehouse />,
      },
      {
        path: 'claims',
        element: <Claims />,
        loader: policyLoader,
      },
      {
        path: 'transactions',
        element: <Transactions />,
        loader: policyLoader,
      },
      {
        path: 'erp',
        element: <ExtendedReportingPeriod />,
        loader: policyLoader,
      },
    ],
  },
]);
